import React, { useState } from "react";
import { Mail, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

/**
 * Banner shown on Dashboard when the current user has not yet verified
 * their email. Rendered only when:
 *   • user exists
 *   • user.email_verified_at is null
 *
 * Users who registered via Google OAuth already have email_verified_at
 * set (Google verifies for us), so this banner naturally does not
 * appear for them — no need to check auth_providers explicitly.
 *
 * One CTA: resend the verification email. We rate-limit the button
 * locally (60-second cooldown) on top of the backend's 3/hour limit
 * so accidental double-clicks don't burn through the quota.
 */
const UnverifiedBanner = () => {
    const { user } = useAuth();
    const [sending, setSending] = useState(false);
    const [sentAt, setSentAt] = useState(null);

    if (!user) return null;
    if (user.email_verified_at) return null;

    const cooledDown = !sentAt || (Date.now() - sentAt) > 60_000;

    const handleResend = async () => {
        if (sending || !cooledDown) return;
        setSending(true);
        try {
            const r = await api.post("/auth/resend-verification");
            if (r.data?.already_verified) {
                toast.success("Ваш email уже підтверджено. Оновіть сторінку.");
            } else {
                toast.success("Лист надіслано. Перевірте папку «Спам», якщо не бачите його у вхідних.");
            }
            setSentAt(Date.now());
        } catch (err) {
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            if (status === 429) {
                toast.error("Забагато запитів. Спробуйте через годину.");
            } else {
                toast.error(typeof detail === "string" ? detail : "Не вдалося надіслати лист. Спробуйте пізніше.");
            }
        } finally {
            setSending(false);
        }
    };

    return (
        <div
            className="rounded-xl border border-warning/40 bg-warning/5 px-4 py-3 flex items-start gap-3"
            data-testid="unverified-banner"
        >
            <Mail className="text-warning flex-shrink-0 mt-0.5" size={18} />
            <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-text-primary">
                    Підтвердіть email
                </div>
                <div className="text-xs text-secondary-c mt-1 leading-relaxed">
                    Ми надіслали лист на <span className="font-mono">{user.email}</span>.
                    Натисніть посилання у листі, щоб активувати акаунт.
                </div>
            </div>
            <button
                onClick={handleResend}
                disabled={sending || !cooledDown}
                data-testid="unverified-resend"
                className="flex-shrink-0 text-xs font-medium px-3 py-1.5 rounded-md border border-warning/40 bg-bg-base hover:bg-surface transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
            >
                {sending && <Loader2 className="animate-spin" size={12} />}
                {!sending && sentAt && <CheckCircle2 size={12} className="text-success" />}
                {sentAt && !sending && cooledDown ? "Надіслати ще раз"
                    : sentAt && !cooledDown ? "Надіслано"
                    : "Надіслати лист"}
            </button>
        </div>
    );
};

export default UnverifiedBanner;
